// Maps a weekly quest's pick state, and the gameweek's graded result, onto the props QuestCard
// renders. Its own module so the hit/miss/unplayed branches per quest type are unit testable
// without mounting the grid; reads the
// server's persisted `QuestResult` instead of `mockQuestResults`' seeded hash.
import type { QuestDefinition } from '../../quests'
import {
  QUEST_MISS_VERDICT,
  QUEST_POINTS,
  hasQuestPickValue,
  questFigureLine,
  questOutcomeFor,
  type QuestResult,
} from '../../questResults'
import { teamAbbr, type TeamId } from '../../teams'
import type { QuestPick, QuestResultData } from '../QuestCard'
import type { Game, QuestPickState } from '../../types'

export { hasQuestPickValue }

/** The pre-result pick, i.e. what the cta/confirm/locked/dimmed states show. */
export function buildQuestPick(
  quest: QuestDefinition,
  games: Game[],
  state: QuestPickState | undefined
): QuestPick {
  if (quest.type === 'team') return { type: 'team', team: state?.team }
  if (quest.type === 'match') {
    const game = state?.gameId ? games.find((g) => g.id === state.gameId) : undefined
    return { type: 'match', game }
  }
  return {
    type: 'over_under',
    line: quest.line ?? 0,
    eyebrow: quest.eyebrow ?? '',
    side: state?.side,
  }
}

/**
 * Whether this player's card can show a verdict yet.
 *
 * A result exists is no longer the same question as this player's answer is final: a fixture-scoped
 * quest is graded from the fixtures that have reached half-time, so a pick on a match that has not
 * kicked off has a `partial` result and no verdict in it. `questOutcomeFor` returns `undefined`
 * there, and `buildQuestResult` would read that as "unplayed" — an empty card claiming the round is
 * over — so the caller has to ask this first and keep the card sealed instead.
 */
export function questResultIsShowable(
  quest: QuestDefinition,
  result: QuestResult | undefined,
  state: QuestPickState | undefined
): boolean {
  if (!result) return false
  return result.partial !== true || questOutcomeFor(quest, result, state) !== undefined
}

function fixtureLabel(game: Game | undefined): string | undefined {
  return game ? `${teamAbbr(game.homeTeam)} v ${teamAbbr(game.awayTeam)}` : undefined
}

/**
 * The settled result: outcome plus the pick/result pair QuestCard's `phase="result"` renders.
 *
 * Four outcomes, not three. Beside hit, miss and the unplayed card that shows the
 * true answer instead of a pick, there is **void**: the round's data did not support grading —
 * no statistics published for a fixture, no events, an unattributable goal — so the card reads
 * "no result" and awards nothing to anyone. A quest that is merely *ungraded* never reaches here:
 * the caller keeps it sealed.
 *
 * Joint winners are the norm rather than the exception ("most corners" ties often, and half a
 * dozen teams lead at half-time), so the pick slot on a miss or an unplayed card shows the best
 * of them — `winningTeams`/`winningGameIds` are ordered best-first by the grader.
 */
export function buildQuestResult(
  quest: QuestDefinition,
  games: Game[],
  state: QuestPickState | undefined,
  questResult: QuestResult
): { pick: QuestPick; result: QuestResultData } {
  const outcome = questOutcomeFor(quest, questResult, state) ?? 'unplayed'
  const points = outcome === 'hit' ? QUEST_POINTS : 0
  const base = { outcome, points, pointsAvailable: QUEST_POINTS }
  const figures = questResult.figures ?? {}
  const isVoid = outcome === 'void'

  if (quest.type === 'team') {
    const winner = questResult.winningTeams?.[0]
    // A quest with no winner says so on a miss and stops there — `questResult.value` would be the
    // best figure in the round, which for `frontRunners` is another club's lead in another fixture.
    //
    const missVerdict = outcome === 'miss' ? QUEST_MISS_VERDICT[quest.id] : undefined
    return {
      pick: {
        type: 'team',
        // Nobody's own pick to show on an unplayed card, so the true answer takes the slot. A
        // voided quest has no true answer either — it shows whatever the player picked.
        team: outcome === 'unplayed' ? winner : state?.team,
      },
      result: {
        ...base,
        statLine: isVoid
          ? 'No result'
          : outcome === 'hit'
            ? questFigureLine(quest.id, figures[state?.team as TeamId])
            : (missVerdict ?? questFigureLine(quest.id, questResult.value)),
        // Nothing to caption once the big slot carries a verdict instead of a figure.
        statCaption: missVerdict ? null : undefined,
      },
    }
  }

  if (quest.type === 'match') {
    const winningGame = games.find((g) => g.id === questResult.winningGameIds?.[0])
    const yourGame = state?.gameId ? games.find((g) => g.id === state.gameId) : undefined
    const yourFigure = state?.gameId ? figures[state.gameId] : undefined
    const missVerdict = outcome === 'miss' ? QUEST_MISS_VERDICT[quest.id] : undefined
    return {
      pick: { type: 'match', game: outcome === 'unplayed' ? winningGame : yourGame },
      result: {
        ...base,
        truthLine: isVoid
          ? 'No result'
          : outcome === 'hit'
            ? questFigureLine(quest.id, yourFigure)
            : // A quest with no winner has no fixture to name — `firstHalfAction`'s `value` is
              // whichever fixture had the most first-half goals, which beat nobody.
              (missVerdict ??
              // Naming the fixture is the whole point of a miss's truth line on the quests that do
              // have a winner: the figure alone says nothing about which match produced it.
              [fixtureLabel(winningGame), questFigureLine(quest.id, questResult.value)]
                .filter(Boolean)
                .join(' · ')),
        // Suppressed alongside the verdict — "Your pick had 1" is a figure too, and the verdict
        // already says everything the miss has to say.
        yourFigureLine:
          outcome === 'miss' && !missVerdict && yourFigure !== undefined
            ? `Your pick had ${yourFigure}`
            : undefined,
      },
    }
  }

  return {
    pick: {
      type: 'over_under',
      line: quest.line ?? 0,
      eyebrow: quest.eyebrow ?? '',
      side: outcome === 'unplayed' ? undefined : state?.side,
    },
    result: {
      ...base,
      trueSide: questResult.trueSide,
      trueValueLine: isVoid ? 'No result' : questFigureLine(quest.id, questResult.value),
    },
  }
}
